
// @ts-check

class ProductShowcaseGallery extends HTMLElement {
  constructor() {
    super();
    /** @type {string[] | null} */
    this._originalImages = null;
  }

  connectedCallback() {
    this.captureOriginalImagesIfNeeded();

    const dataAttr = this.getAttribute('data-variant-gallery');
    if (dataAttr) {
      try {
        const urls = JSON.parse(dataAttr);
        if (Array.isArray(urls) && urls.length > 0) {
          this.applyImages(urls);
        }
      } catch (e) {
        console.error('[ProductShowcaseGallery] Error parsing data-variant-gallery', e);
      }
    }

    ProductShowcaseGallery.ensureVariantChangeListener();
  }

  /**
   * @param {string[]} urls
   */
  setVariantImages(urls) {
    if (!Array.isArray(urls)) return;
    this.applyImages(urls);
  }

  /**
   * Applies URLs to the unified carousel slides.
   * Updates both main slide images and thumbnail images.
   * @param {string[]} urls
   */
  applyImages(urls) {
    /** @type {HTMLImageElement[]} */
    const imageEls = [];
    const slides = this.querySelectorAll('.product-showcase__gallery--main .swiper-slide');

    slides.forEach(slide => {
      const img = /** @type {HTMLImageElement|null} */(slide.querySelector('img.product-showcase__image'));
      if (img) imageEls.push(img);
    });

    imageEls.forEach((img, index) => {
      const url = urls && urls[index];
      if (url) {
        img.src = url;
        img.removeAttribute('data-placeholder');
        img.style.opacity = '';
      } else if (this._originalImages && this._originalImages[index]) {
        img.src = this._originalImages[index];
        img.style.opacity = '';
      }
    });

    this._rebuildThumbs(urls);
    document.dispatchEvent(new CustomEvent('pdp:images-applied'));
  }

  /**
   * Updates thumbnail images when variant images change.
   * @param {string[]} urls
   */
  _rebuildThumbs(urls) {
    if (!urls || !urls.length) return;
    const thumbSlides = this.querySelectorAll('.product-showcase__gallery--thumbs .swiper-slide');
    thumbSlides.forEach((slide, index) => {
      const img = /** @type {HTMLImageElement|null} */(slide.querySelector('img.product-showcase__thumb-image'));
      if (img && urls[index]) {
        img.src = urls[index];
      }
    });

    const psw = /** @type {any} */(window).__psw;
    if (psw) {
      try { if (psw.thumbsSwiper) psw.thumbsSwiper.update(); } catch (_) {}
      try { if (psw.mainSwiper) psw.mainSwiper.update(); } catch (_) {}
    }
  }

  captureOriginalImagesIfNeeded() {
    if (this._originalImages) return;
    /** @type {string[]} */
    const originals = [];
    const slides = this.querySelectorAll('.product-showcase__gallery--main .swiper-slide');
    slides.forEach(slide => {
      const img = /** @type {HTMLImageElement|null} */(slide.querySelector('img.product-showcase__image'));
      if (img && img.src) originals.push(img.src);
    });
    if (originals.length > 0) this._originalImages = originals;
  }

  updateFromActiveVariant() {
    const sizeRoot = document.querySelector('.product-variants--size');
    let galleryData = null;

    if (!sizeRoot) {
      galleryData = this.getAttribute('data-variant-gallery');
    } else {
      const activeButton =
        sizeRoot.querySelector('.variant-product.variant-product-selected .variant-product__button') ||
        sizeRoot.querySelector('.variant-product__button');
      if (activeButton) {
        galleryData = activeButton.getAttribute('data-variant-gallery');
      }
      if (!galleryData) {
        galleryData = this.getAttribute('data-variant-gallery');
      }
    }

    if (!galleryData) return;
    try {
      const urls = JSON.parse(galleryData);
      if (Array.isArray(urls) && urls.length > 0) {
        this.setVariantImages(urls);
      }
    } catch (e) {
      console.error('[ProductShowcaseGallery] Error parsing data-variant-gallery (variant update)', e);
    }
  }

  static ensureVariantChangeListener() {
    if (ProductShowcaseGallery._variantListenerAttached) return;

    const handler = () => {
      const nodes = document.querySelectorAll('product-showcase-gallery');
      nodes.forEach(el => {
        const inst = /** @type {any} */(el);
        if (typeof inst.updateFromActiveVariant === 'function') {
          inst.updateFromActiveVariant();
        }
      });
    };

    document.addEventListener('pdp:product-changed', handler);
    document.addEventListener('pdp:gallery-updated', handler);

    ProductShowcaseGallery._variantListenerAttached = true;
  }
}

ProductShowcaseGallery._variantListenerAttached = false;

if (!window.customElements.get('product-showcase-gallery')) {
  window.customElements.define('product-showcase-gallery', ProductShowcaseGallery);
}
